import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, ChevronDown, ChevronUp, Hash, ExternalLink } from 'lucide-react';
import Button from '../../../components/ui/Button';

const STATUS_STYLES = {
  ACTIVE: 'bg-green-100 text-green-800',
  PENDING: 'bg-amber-100 text-amber-800',
  REJECTED: 'bg-red-100 text-red-800',
};

/**
 * Duplicate vendor rows, grouped by company.
 *
 * Grouped for the same reason conflicts are: one company can own many rows (ACAMA has
 * three), and listing each separately made the screen look like it was repeating itself.
 *
 * These rows are not necessarily junk — a company registered in several states holds one
 * ERP code per GST registration — so each row offers "Assign code", which lists the ERP
 * codes still free for that company.
 */
const DuplicateVendorList = ({
  groups,
  rowTotal,
  total,
  loading,
  page,
  totalPages,
  onPageChange,
  onAssign,
}) => {
  const [expandedKey, setExpandedKey] = useState(null);

  if (loading && groups.length === 0) {
    return <div className="text-center py-16 text-gray-500">Loading…</div>;
  }

  if (groups.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-lg border">
        <Copy className="h-10 w-10 text-green-500 mx-auto mb-2" />
        <p className="text-gray-700 font-medium">No duplicate vendor rows</p>
        <p className="text-gray-500 text-sm">Every code-less vendor is a distinct company.</p>
      </div>
    );
  }

  return (
    <>
      <p className="text-xs text-gray-500 mb-2">
        {rowTotal} rows across {total} companies. Each shares a name with a row that already
        holds the ERP code — assign a free ERP code if it is a separate registration, otherwise
        merge or remove it.
      </p>

      <div className="space-y-3">
        {groups.map((group) => {
          const isOpen = expandedKey === group.company_key;
          const rows = group.rows || [];
          return (
            <div key={group.company_key} className="bg-white rounded-lg border shadow-sm">
              <button
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
                onClick={() => setExpandedKey(isOpen ? null : group.company_key)}
              >
                <div className="min-w-0">
                  <span className="font-semibold text-gray-900">{group.company_name}</span>
                  <span className="ml-3 text-sm text-gray-500 whitespace-nowrap">
                    holder{' '}
                    <span className="font-mono font-semibold text-gray-700">{group.holder_code}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-sm bg-orange-100 text-orange-800 rounded-full px-2.5 py-0.5 whitespace-nowrap">
                    {rows.length} duplicate{rows.length > 1 ? 's' : ''}
                  </span>
                  {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
              </button>

              {isOpen && (
                <div className="border-t">
                  <div className="px-4 py-2.5 bg-slate-50 border-b text-sm text-gray-600 flex items-center gap-2 flex-wrap">
                    <Hash className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <span>Code held by</span>
                    <Link
                      to={`/vendor-profile-details/${group.holder_id}`}
                      className="text-blue-600 hover:underline font-medium"
                    >
                      #{group.holder_id} {group.holder_name}
                    </Link>
                    <span className="font-mono font-semibold text-gray-900">{group.holder_code}</span>
                  </div>

                  {rows.map((d) => (
                    <div key={d.id} className="px-4 py-3 border-b last:border-b-0">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-900">#{d.id}</span>
                            <span
                              className={`text-xs rounded-full px-2 py-0.5 ${
                                STATUS_STYLES[d.status] || 'bg-slate-100 text-slate-700'
                              }`}
                            >
                              {d.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 break-all mt-0.5">{d.email}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Link
                            to={`/vendor-profile-details/${d.id}`}
                            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                          >
                            Open <ExternalLink className="h-3.5 w-3.5" />
                          </Link>
                          <Button variant="outline" size="sm" onClick={() => onAssign(d)}>
                            <Hash className="h-3.5 w-3.5 mr-1" /> Assign code
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <Button variant="outline" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
            Previous
          </Button>
          <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
          <Button variant="outline" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
            Next
          </Button>
        </div>
      )}
    </>
  );
};

export default DuplicateVendorList;
